import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import SiteLayout from "@/components/layout/SiteLayout";
import AdminLayout from "@/components/admin/AdminLayout";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import About from "@/pages/About";
import LabTesting from "@/pages/LabTesting";
// import COALibrary from "@/pages/COALibrary"; // HIDDEN: uncomment to restore COA Library page
import FAQ from "@/pages/FAQ";
// import ResearchLibrary from "@/pages/ResearchLibrary"; // HIDDEN: uncomment to restore Research Library page
// import BlogPost from "@/pages/BlogPost"; // HIDDEN
// import Blog from "@/pages/Blog"; // HIDDEN
import Contact from "@/pages/Contact";
import AffiliateProgram from "@/pages/AffiliateProgram";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import ShippingPolicy from "@/pages/ShippingPolicy";
import ReturnsPolicy from "@/pages/ReturnsPolicy";
import ResearchUseDisclaimer from "@/pages/ResearchUseDisclaimer";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AccountDashboard from "@/pages/account/AccountDashboard";
import AccountOrderDetail from "@/pages/account/AccountOrderDetail";
import NotFound from "@/pages/NotFound";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductForm from "@/pages/admin/AdminProductForm";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminBlogForm from "@/pages/admin/AdminBlogForm";
import AdminFAQs from "@/pages/admin/AdminFAQs";
import AdminAffiliates from "@/pages/admin/AdminAffiliates";
import AdminDiscountSettings from "@/pages/admin/AdminDiscountSettings";
import AdminDiscountRequests from "@/pages/admin/AdminDiscountRequests";
import AdminQuoteRequests from "@/pages/admin/AdminQuoteRequests";
import AdminRestockWaitlist from "@/pages/admin/AdminRestockWaitlist";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminTaxReport from "@/pages/admin/AdminTaxReport";
import AdminShippingSettings from "@/pages/admin/AdminShippingSettings";

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route element={<SiteLayout />}>
              <Route index element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/product/:slug" element={<ProductDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/lab-testing" element={<LabTesting />} />
              {/* HIDDEN: uncomment to restore COA Library page */}
              {/* <Route path="/coa-library" element={<COALibrary />} /> */}
              <Route path="/faq" element={<FAQ />} />
              {/* HIDDEN: uncomment to restore Research Library pages */}
              {/* <Route path="/research" element={<ResearchLibrary />} /> */}
              {/* <Route path="/blog" element={<Blog />} /> */}
              {/* <Route path="/blog/:slug" element={<BlogPost />} /> */}
              <Route path="/contact" element={<Contact />} />
              <Route path="/affiliate-program" element={<AffiliateProgram />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/shipping-policy" element={<ShippingPolicy />} />
              <Route path="/returns-policy" element={<ReturnsPolicy />} />
              <Route path="/research-use-disclaimer" element={<ResearchUseDisclaimer />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order-confirmation/:orderNumber" element={<OrderConfirmation />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/account" element={<ProtectedRoute><AccountDashboard /></ProtectedRoute>} />
              <Route path="/account/orders/:orderNumber" element={<ProtectedRoute><AccountOrderDetail /></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
            </Route>

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="products/new" element={<AdminProductForm />} />
              <Route path="products/:id/edit" element={<AdminProductForm />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="orders/:id" element={<AdminOrderDetail />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="shipping" element={<AdminShippingSettings />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="blog/new" element={<AdminBlogForm />} />
              <Route path="blog/:id/edit" element={<AdminBlogForm />} />
              <Route path="faqs" element={<AdminFAQs />} />
              <Route path="affiliates" element={<AdminAffiliates />} />
              <Route path="discount-settings" element={<AdminDiscountSettings />} />
              <Route path="discount-requests" element={<AdminDiscountRequests />} />
              <Route path="quote-requests" element={<AdminQuoteRequests />} />
              <Route path="restock-waitlist" element={<AdminRestockWaitlist />} />
              <Route path="newsletter" element={<AdminNewsletter />} />
              <Route path="tax-report" element={<AdminTaxReport />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
